import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Heart, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SectionHeading } from "./Story";
import { getGuestbook, createGuestbookEntry } from "@/lib/api";

export const Guestbook = ({ guestbook }) => {
  const [entries, setEntries] = useState([]);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => getGuestbook().then(setEntries).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) {
      toast.error("Please add your name and a short wish.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await createGuestbookEntry({ name: name.trim(), message: message.trim() });
      setName(""); setMessage("");
      if (res.pending) {
        toast.success("Thank you! Your wish will appear once the couple approves it. 💛");
      } else {
        toast.success("Thank you for your beautiful wish! 💛");
        load();
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="guestbook" className="py-20 sm:py-28 lg:py-32 bg-[var(--ivory)]" data-testid="guestbook-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-8">
        <SectionHeading script={guestbook?.subtitle || "leave a note"} title={guestbook?.title || "Guestbook"} testid="guestbook-heading" />
        {guestbook?.description && (
          <p className="text-center text-[var(--text-secondary)] max-w-2xl mx-auto -mt-6 mb-10 leading-relaxed">{guestbook.description}</p>
        )}

        <form onSubmit={submit} className="bg-[var(--card)] rounded-2xl p-6 sm:p-8 border border-[var(--border)] shadow-sm max-w-xl mx-auto space-y-4" data-testid="guestbook-form">
          <div>
            <Label>Your Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" maxLength={80} className="mt-1.5" data-testid="guestbook-name-input" />
          </div>
          <div>
            <Label>Your Wish</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Share a blessing for Rishi & Priyanka..." maxLength={500} className="mt-1.5" data-testid="guestbook-message-input" />
          </div>
          <button type="submit" disabled={submitting} className="w-full rounded-full py-3 font-medium flex items-center justify-center gap-2 transition-all hover:scale-[1.02] disabled:opacity-60" style={{ backgroundColor: "var(--gold)", color: "var(--on-gold)" }} data-testid="guestbook-submit-button">
            <Send className="w-4 h-4" /> {submitting ? "Sending..." : "Leave a wish"}
          </button>
        </form>

        {entries.length > 0 && (
          <div className="mt-12 columns-1 sm:columns-2 lg:columns-3 gap-4 [&>*]:mb-4" data-testid="guestbook-wall">
            {entries.map((en, i) => (
              <motion.div
                key={en.id || i}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }}
                className="break-inside-avoid rounded-2xl p-5 border border-[var(--border)] bg-[var(--card)] shadow-sm"
                data-testid={`guestbook-entry-${i}`}
              >
                <Heart className="w-4 h-4 text-[var(--gold)]" />
                <p className="mt-2 text-[var(--text-secondary)] leading-relaxed whitespace-pre-line">{en.message}</p>
                <p className="mt-3 font-serif text-lg text-[var(--burgundy)]">— {en.name}</p>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
